import socket, { connectWithAuth } from "../socket";
import React, { useEffect, useState, useRef } from 'react'
import { useNavigate, useParams, Link } from 'react-router-dom';
import GuestService from '../services/guestService';
import { useDispatch, useSelector } from 'react-redux';
import { loggedGuest, refreshDataGuest, loggOutGuest } from '../store/guestSlice';
import { useTranslation } from "react-i18next";
import Cookies from 'js-cookie';
import LoadingForButton from '../components/LoadingForButton';
import TablesReservation from '../components/TablesReservation';
import NotificationBanner from "../components/NotificationBanner";
import { FaCalendarAlt, FaMapMarkerAlt, FaSignOutAlt, FaChevronUp, FaChevronDown, FaCheck, FaTimes, FaUser, FaUserFriends, FaEdit, FaSearch, FaPlus, FaMinus } from 'react-icons/fa';
import { Helmet } from "react-helmet";
import Footer from "../components/Footer";
import FamilyReservationFlow from '../components/FamilyReservationFlow';

// ─── STEPS ───────────────────────────────────────────────────────────────────
const STEPS = {
    ATTENDANCE: 'attendance',       // Korak 1: Dolazim / Ne dolazim
    GROUP: 'group',                 // Korak 2: S kim dolaziš?
    TABLE: 'table',                 // Korak 3: Odabir stola
    CONFIRMED: 'confirmed',         // Potvrđeno stanje
    ADDED_BY_GUEST: 'addedByGuest', // Netko te dodao za stol
};

const MAX_WITHOUT_INVITE = 5;

function ReservationPage() {
    const { t, i18n } = useTranslation();
    const { weddingId, code } = useParams();
    const navigate = useNavigate();
    const dispatch = useDispatch();

    // ─── Redux ───────────────────────────────────────────────────────────────
    const guest = useSelector((state) => state.guestStore.guest);
    const isGuestLoggedIn = useSelector((state) => state.guestStore.isGuestLoggedIn);
    const isGuestAuthLoading = useSelector((state) => state.guestStore.isGuestAuthLoading);
    const refreshData = useSelector((state) => state.guestStore.refreshData);

    // ─── UI state ────────────────────────────────────────────────────────────
    const [step, setStep] = useState(STEPS.ATTENDANCE);
    const [loading, setLoading] = useState(false);
    const [rsvpLoading, setRsvpLoading] = useState(false);
    const [openDropdown, setOpenDropdown] = useState(null);
    const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
    const [showNotification, setShowNotification] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState('');
    const [errorResponse, setErrorResponse] = useState(false);
    const [showNotAttendingConfirm, setShowNotAttendingConfirm] = useState(false);
    // [NOVO] Modal za potvrdu izlaska iz grupe
    const [showCancelGroupConfirm, setShowCancelGroupConfirm] = useState(false);
    const [groupMode, setGroupMode] = useState(null); // null | 'solo' | 'group'
    const [lightboxOpen, setLightboxOpen] = useState(false);

    // ─── Data ────────────────────────────────────────────────────────────────
    const [weddingInfo, setWeddingInfo] = useState({});
    const [tablesWithGuests, setTablesWithGuests] = useState([]);

    // ─── Group state (Korak 2) ───────────────────────────────────────────────
    const [groupSearch, setGroupSearch] = useState('');
    const [groupSearchResults, setGroupSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [selectedGuests, setSelectedGuests] = useState([]); // gosti s popisa
    const [withoutInviteCount, setWithoutInviteCount] = useState(0); // dodatna mjesta
    const [familyShowInvitation, setFamilyShowInvitation] = useState(true);

    const searchRef = useRef(null);
    const searchTimeout = useRef(null);

    const notificationTimer = useRef(null);

    const capitalizeWords = (str) =>
        str?.toLowerCase().split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

    const totalGroupSize = 1 + selectedGuests.length + withoutInviteCount;

    // ─── Inicijalni step iz stanja gosta ─────────────────────────────────────
    useEffect(() => {
        if (!isGuestLoggedIn) return;
        if (guest.addedByGuestName && !guest.rsvpDate) {
            setStep(STEPS.ADDED_BY_GUEST);
        } else if (guest.rsvpDate) {
            setStep(STEPS.CONFIRMED);
        } else {
            setStep(STEPS.ATTENDANCE);
        }
    }, [isGuestLoggedIn, guest._id]);

    // ─── Auto-login ───────────────────────────────────────────────────────────
    useEffect(() => {
        if (!code) return;
        if (isGuestAuthLoading) return;
        Cookies.remove('gAuthToken');
        Cookies.remove('authToken');
        dispatch(loggOutGuest());
        setLoading(true);
        GuestService.loginGuest({ password: code, weddingId })
            .then((res) => {
                if (res.status === 200) {
                    Cookies.set('gAuthToken', res.data.token, { path: '/' });
                    connectWithAuth();
                    dispatch(loggedGuest(res.data.guest));
                }
                setLoading(false);
            })
            .catch(() => setLoading(false));
    }, [weddingId, code, isGuestAuthLoading]);

    // ─── Wedding info + stolovi ───────────────────────────────────────────────
    useEffect(() => {
        const gToken = Cookies.get('gAuthToken');
        if (isGuestLoggedIn && weddingId === guest.weddingId && gToken) {
            GuestService.getPublicWeddingInfo(weddingId)
                .then((res) => {
                    setWeddingInfo(res.data.wedding);
                    const gToken = Cookies.get('gAuthToken');
                    if (isGuestLoggedIn && weddingId === guest.weddingId && gToken) {
                        GuestService.getWeddingDetailsForGuest(weddingId)
                            .then((res) => setTablesWithGuests(res.data.tablesWithGuests))
                            .catch(() => { });
                    }
                })
                .catch((err) => {
                    if (err.response?.status === 400) navigate('/');
                });
        }

    }, [weddingId, isGuestLoggedIn, refreshData]);

    // ─── Socket ───────────────────────────────────────────────────────────────
    useEffect(() => {
        if (!isGuestLoggedIn || !weddingId) return;
        if (weddingId !== guest.weddingId) {
            dispatch(loggOutGuest());
            navigate('/');
            return;
        }
        const token = Cookies.get('gAuthToken');
        if (token && !socket.connected) connectWithAuth();

        const handleConnect = () => socket.emit('joinWeddingRoom', { weddingId, isAdmin: false });
        const handleTableUpdate = (data) => {
            setTablesWithGuests(data);
            setNotificationMessage(t('dataUpdated'));
            setShowNotification(true);
            if (notificationTimer.current) clearTimeout(notificationTimer.current);
            notificationTimer.current = setTimeout(() => setShowNotification(false), 3000);
            if (isGuestLoggedIn) dispatch(refreshDataGuest());
        };

        socket.on('connect', handleConnect);
        socket.on('tableUpdate', handleTableUpdate);
        if (socket.connected) handleConnect();

        return () => {
            socket.off('connect', handleConnect);
            socket.off('tableUpdate', handleTableUpdate);
            if (notificationTimer.current) clearTimeout(notificationTimer.current);
        };
    }, [weddingId, isGuestLoggedIn, guest.weddingId]);

    // ─── Inactivity logout ────────────────────────────────────────────────────
    useEffect(() => {
        if (!isGuestLoggedIn) return;
        let timer;
        const logout = () => { dispatch(loggOutGuest()); socket.disconnect(); navigate('/'); };
        const reset = () => { clearTimeout(timer); timer = setTimeout(logout, 15 * 60 * 1000); };
        const events = ['mousemove', 'keydown', 'scroll', 'click'];
        events.forEach(e => window.addEventListener(e, reset));
        reset();
        return () => { clearTimeout(timer); events.forEach(e => window.removeEventListener(e, reset)); };
    }, [isGuestLoggedIn]);

    // ─── Autocomplete za grupu ────────────────────────────────────────────────
    useEffect(() => {
        if (!groupSearch.trim() || groupSearch.trim().length < 2) {
            setGroupSearchResults([]);
            return;
        }
        if (searchTimeout.current) clearTimeout(searchTimeout.current);
        searchTimeout.current = setTimeout(() => {
            setSearchLoading(true);
            GuestService.searchGuests(weddingId, groupSearch)
                .then((res) => {
                    const filtered = res.data.guests.filter(g =>
                        g._id !== guest._id &&
                        !selectedGuests.find(sg => sg._id === g._id) &&
                        !g.reservedTableId &&
                        g.companions?.registered?.length === 0 &&
                        g.companions?.unregistered === 0
                    );
                    setGroupSearchResults(filtered);
                    setSearchLoading(false);
                })
                .catch(() => setSearchLoading(false));
        }, 300);
        return () => clearTimeout(searchTimeout.current);
    }, [groupSearch, selectedGuests]);

    // ─── Klik izvan autocomplete ──────────────────────────────────────────────
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (searchRef.current && !searchRef.current.contains(e.target))
                setGroupSearchResults([]);
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // ─── Max slobodnih mjesta (za prikaz koji stolovi su dostupni) ────────────
    const maxFreeSeatsAnyTable = tablesWithGuests.length
        ? Math.max(0, ...tablesWithGuests.filter(t => !t.isLocked).map(t => t.seats - t.reservedSeats))
        : 0;

    const currentTable = tablesWithGuests?.find(t => t._id === guest.reservedTableId);
    const freeSeatsOnCurrentTable = currentTable
        ? (currentTable.seats - currentTable.reservedSeats) + (guest.companions?.unregistered || 0)
        : 0;
    const maxCanAdd = guest.reservedTableId
        ? freeSeatsOnCurrentTable
        : maxFreeSeatsAnyTable - 1;

    const showInvitation = guest.isFamilyGroup ? familyShowInvitation : [STEPS.ATTENDANCE, STEPS.CONFIRMED, STEPS.ADDED_BY_GUEST].includes(step);

    // ─── Handlers ─────────────────────────────────────────────────────────────

    const handleLogout = () => {
        dispatch(loggOutGuest());
        setTablesWithGuests([]);
        setShowLogoutConfirm(false);
        navigate('/');
    };

    // Korak 1: potvrda dolaska
    const handleAttendance = (attending) => {
        if (!attending) {
            setRsvpLoading(true);
            GuestService.confirmAttendance({
                isAttending: false,
            })
                .then(() => {
                    dispatch(refreshDataGuest());
                    setStep(STEPS.CONFIRMED);
                    setRsvpLoading(false);
                })
                .catch(() => setRsvpLoading(false));
            return;
        }
        // Učitaj postojeće registered companions u lokalni state
        const existingRegistered = guest.companions?.registered?.filter(r => !r.reservedTableId) || [];
        setGroupMode(null);
        setSelectedGuests(existingRegistered);
        setWithoutInviteCount(guest.companions?.unregistered || 0);
        setGroupSearch('');
        setStep(STEPS.GROUP);
    };

    // Korak 2 → Korak 3 (solo ili s grupom)
    const handleGroupConfirm = () => {
        setRsvpLoading(true);
        GuestService.confirmAttendance({
            isAttending: true,
            groupGuestIds: selectedGuests.map(g => g._id),
            withoutInviteCount,
        })
            .then(() => {
                setStep(STEPS.CONFIRMED);
                dispatch(refreshDataGuest());
                setRsvpLoading(false);
            })
            .catch(() => setRsvpLoading(false));
    };

    // Solo — preskoči grupu, spremi odmah i idi na stol
    const handleSolo = () => {
        setRsvpLoading(true);
        GuestService.confirmAttendance({
            isAttending: true,
            groupGuestIds: [],
            withoutInviteCount: 0,
        })
            .then(() => {
                setStep(STEPS.CONFIRMED);
                dispatch(refreshDataGuest());
                setRsvpLoading(false);
            })
            .catch(() => setRsvpLoading(false));
    };

    const handleConfirmAddedByGuest = () => {
        setRsvpLoading(true);
        GuestService.confirmAttendance({ isAttending: true })
            .then(() => {
                setStep(STEPS.CONFIRMED);
                dispatch(refreshDataGuest());
                setRsvpLoading(false);
            })
            .catch(() => setRsvpLoading(false));
    };

    const handleCancelGroup = () => {
        setRsvpLoading(true);
        GuestService.cancelGroup()
            .then(() => {
                dispatch(refreshDataGuest());
                setRsvpLoading(false);
                setStep(STEPS.ATTENDANCE);
            })
            .catch(() => setRsvpLoading(false));
    };

    const addGuest = (g) => {
        const currentTotal = selectedGuests.length + withoutInviteCount;
        if (currentTotal >= maxCanAdd) return; // tiha zaštita
        setSelectedGuests(prev => [...prev, g]);
        setGroupSearch('');
        setGroupSearchResults([]);
    };

    const removeGuest = (id) =>
        setSelectedGuests(prev => prev.filter(g => g._id !== id));

    // ─── RENDER HELPERS ───────────────────────────────────────────────────────
    const renderHeader = () => (
        <>
            {/* Lightbox */}
            {lightboxOpen && weddingInfo?.invitationUrl && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 px-4"
                    onClick={() => setLightboxOpen(false)}
                >
                    <div className="relative max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
                        <button
                            onClick={() => setLightboxOpen(false)}
                            className="absolute -top-10 right-0 text-white text-2xl hover:text-gray-300 transition"
                        >
                            <FaTimes />
                        </button>
                        <img
                            src={weddingInfo.invitationUrl}
                            alt="Pozivnica"
                            className="w-full rounded-2xl shadow-2xl object-contain"
                        />
                    </div>
                </div>
            )}

            {!weddingInfo?.invitationUrl ? (
                <div className="w-full mb-4 px-4">
                    <h1 className="text-center text-2xl lg:text-4xl font-bold text-gray-800 capitalize mb-3">
                        {weddingInfo.brideName} & {weddingInfo.groomName}
                    </h1>
                    <div className="flex items-center justify-around">
                        <span className="flex items-center gap-1 lg:text-xl">
                            <FaCalendarAlt className="text-sm lg:text-lg" />
                            {new Date(weddingInfo?.date).toLocaleDateString('hr-HR')}
                        </span>
                        <span className="capitalize flex items-center gap-1 lg:text-xl">
                            <FaMapMarkerAlt className="text-sm lg:text-lg" />
                            {weddingInfo.location}, {weddingInfo.city}
                        </span>
                    </div>
                </div>
            ) : (
                <div
                    className={`flex items-center justify-center px-4 overflow-hidden transition-all duration-500 ease-in-out ${showInvitation
                        ? 'max-h-[500px] opacity-100 mb-8 mt-4 lg:max-h-[80vh] lg:mb-0 lg:mt-0 lg:sticky lg:top-6 lg:self-start lg:w-[420px] lg:flex-shrink-0'
                        : 'max-h-0 opacity-0 mb-0 mt-0'
                        }`}
                >
                    <img
                        src={weddingInfo.invitationUrl}
                        alt="Pozivnica"
                        onClick={() => setLightboxOpen(true)}
                        className="w-full max-w-sm rounded-2xl shadow-lg object-contain cursor-pointer hover:scale-[1.02] transition-transform duration-200"
                    />
                </div>
            )}

            {isGuestLoggedIn && (
                <p className={`lg:hidden text-center text-gray-700 font-medium text-lg mb-4 transition-all duration-300 ${showInvitation ? 'opacity-100' : 'opacity-0 h-0 mb-0 overflow-hidden'
                    }`}>
                    👋 {capitalizeWords(guest.fullName)}
                </p>
            )}
        </>
    );

    // ─── KORAK 1: Potvrda dolaska ─────────────────────────────────────────────
    const renderAttendance = () => (
        <div className="flex flex-col items-center px-4 pb-10">
            <div className="w-full max-w-sm flex flex-col gap-4">
                <p className="text-gray-500 text-center text-sm mb-2">
                    {guest.rsvpDate
                        ? 'Možete promijeniti svoju odluku o dolasku.'
                        : 'Potvrdite dolazak na svadbu.'}
                </p>

                <button
                    onClick={() => handleAttendance(true)}
                    disabled={rsvpLoading}
                    className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl text-white font-semibold text-lg bg-gradient-to-r from-rose-500 to-pink-600 shadow-lg shadow-rose-500/30 hover:opacity-90 transition"
                >
                    {rsvpLoading ? <LoadingForButton /> : (
                        guest.isAttending
                            ? <><FaEdit /> Uredi grupu</>
                            : <><FaCheck /> Dolazim</>
                    )}
                </button>

                <button
                    onClick={() => setShowNotAttendingConfirm(true)}
                    disabled={rsvpLoading}
                    className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl text-gray-600 font-semibold text-lg bg-white border border-gray-300 hover:bg-gray-50 transition"
                >
                    {rsvpLoading ? <LoadingForButton /> : <><FaTimes /> Nažalost ne mogu doći</>}
                </button>

                {guest.rsvpDate && (
                    <button
                        onClick={() => setStep(STEPS.CONFIRMED)}
                        className="w-full py-3 text-gray-500 hover:text-gray-700 transition text-center"
                    >
                        ← Natrag
                    </button>
                )}
            </div>
        </div>
    );

    // ─── KORAK 2: Grupa ───────────────────────────────────────────────────────
    const renderGroup = () => {
        const maxUnregistered = Math.max(0, Math.min(MAX_WITHOUT_INVITE, maxCanAdd - selectedGuests.length));
        const canAddMore = (selectedGuests.length + withoutInviteCount) < maxCanAdd;

        // ── groupReservationUsed: true — samo unregistered count ─────────────────
        if (guest.groupReservationUsed || guest.addedByGuestName) {
            return (
                <div className="flex flex-col items-center px-4 pb-10 mt-2">
                    <div className="w-full max-w-sm flex flex-col gap-5">
                        <div className="text-center">
                            <h2 className="text-xl font-bold text-gray-800">Dodatna mjesta</h2>
                            <p className="text-sm text-gray-500 mt-1">
                                Možete promijeniti broj dodatnih mjesta.
                            </p>
                        </div>

                        <div className="flex flex-col gap-2">
                            <label className="text-sm font-semibold text-gray-700">
                                Dodatna mjesta
                            </label>

                            {/* [NOVO] Napomena o dodatnim mjestima */}
                            <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-xl px-3 py-2 text-xs text-blue-700">
                                <span className="mt-0.5 flex-shrink-0">ℹ️</span>
                                <span>
                                    Dodatna mjesta namijenjena su osobama <strong>bez vlastitog linka/pozivnice</strong> (npr. djeca, pratnja). Gosti koji imaju vlastitu pozivnicu trebaju se prijaviti putem svog linka kako ne bi došlo do duplih rezervacija.
                                </span>
                            </div>

                            <div className="flex items-center justify-between bg-white border border-gray-300 rounded-xl px-4 py-3">
                                <span className="text-sm text-gray-600">Broj osoba</span>
                                <div className="flex items-center gap-4">
                                    <button
                                        onClick={() => setWithoutInviteCount(c => Math.max(0, c - 1))}
                                        disabled={withoutInviteCount === 0}
                                        className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:border-rose-400 hover:text-rose-500 disabled:opacity-30 disabled:cursor-not-allowed transition"
                                    >
                                        <FaMinus className="text-xs" />
                                    </button>
                                    <span className="text-lg font-semibold text-gray-800 w-4 text-center">
                                        {withoutInviteCount}
                                    </span>
                                    <button
                                        onClick={() => setWithoutInviteCount(c => Math.min(maxUnregistered, c + 1))}
                                        disabled={withoutInviteCount >= maxUnregistered}
                                        className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:border-rose-400 hover:text-rose-500 disabled:opacity-30 disabled:cursor-not-allowed transition"
                                    >
                                        <FaPlus className="text-xs" />
                                    </button>
                                </div>
                            </div>
                            {/* Preostala mjesta — ispod Dodatna mjesta */}
                            {(() => {
                                const remaining = Math.max(0, maxCanAdd - (selectedGuests.length + withoutInviteCount))
                                if (remaining <= 0) return (
                                    <p className="text-xs text-red-500 px-1">
                                        Dostigli ste maksimalan broj osoba za dostupne stolove.
                                    </p>
                                );
                                return (
                                    <p className="text-xs text-gray-400 px-1">
                                        Možete dodati još{' '}
                                        <span className="font-semibold text-gray-600">
                                            {remaining} {remaining === 1 ? 'osobu' : remaining < 5 ? 'osobe' : 'osoba'}
                                        </span>{' '}
                                        (ovisno o slobodnim mjestima za stolovima).
                                    </p>
                                );
                            })()}

                            {guest.reservedTableId && freeSeatsOnCurrentTable > 0 && (
                                <p className="text-xs text-gray-400 px-1">
                                    Na vašem stolu ima još {freeSeatsOnCurrentTable} slobodnih mjesta.
                                </p>
                            )}
                        </div>

                        <div className="flex flex-col gap-3 mt-1">
                            <button
                                onClick={handleGroupConfirm}
                                disabled={rsvpLoading}
                                className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl text-white font-semibold text-lg bg-gradient-to-r from-rose-500 to-pink-600 shadow-lg shadow-rose-500/30 hover:opacity-90 transition"
                            >
                                {rsvpLoading ? <LoadingForButton /> : <><FaCheck /> Spremi izmjene</>}
                            </button>
                            <button
                                onClick={() => setStep(
                                    guest.rsvpDate ? STEPS.CONFIRMED : STEPS.ATTENDANCE
                                )}
                                className="w-full py-3 text-gray-500 hover:text-gray-700 transition text-center"
                            >
                                ← Natrag
                            </button>
                        </div>
                    </div>
                </div>
            );
        }

        // ── Puni view — groupReservationUsed: false ───────────────────────────────
        return (
            <div className="flex flex-col items-center px-4 pb-10 mt-2">
                <div className="w-full max-w-sm flex flex-col gap-5">

                    {/* ── IZBOR: Solo ili s grupom ── */}
                    {groupMode === null ? (
                        <>
                            <div className="text-center">
                                <h2 className="text-xl font-bold text-gray-800">Kako dolaziš?</h2>
                                <p className="text-sm text-gray-500 mt-1">Odaberi jednu od opcija.</p>
                            </div>

                            <button
                                onClick={handleSolo}
                                disabled={rsvpLoading}
                                className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl text-white font-semibold text-lg bg-gradient-to-r from-rose-500 to-pink-600 shadow-lg shadow-rose-500/30 hover:opacity-90 transition"
                            >
                                {rsvpLoading ? <LoadingForButton /> : <><FaUser /> Dolazim solo</>}
                            </button>

                            <button
                                onClick={() => setGroupMode('group')}
                                className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl text-gray-700 font-semibold text-lg bg-white border border-gray-300 hover:bg-gray-50 transition"
                            >
                                <FaUserFriends /> Dolazim s grupom
                            </button>

                            <button
                                onClick={() => setStep(STEPS.TABLE)}
                                className="w-full py-3 text-rose-500 text-sm underline hover:text-rose-700 transition text-center"
                            >
                                🪑 Pogledaj slobodne stolove prije potvrde
                            </button>

                            <button
                                onClick={() => setStep(guest.rsvpDate ? STEPS.CONFIRMED : STEPS.ATTENDANCE)}
                                className="w-full py-3 text-gray-500 hover:text-gray-700 transition text-center"
                            >
                                ← Natrag
                            </button>
                        </>
                    ) : (
                        /* ── Forma za grupu ── */
                        <>
                            <div className="text-center">
                                <h2 className="text-xl font-bold text-gray-800">S kim dolaziš?</h2>
                                <p className="text-sm text-gray-500 mt-1">
                                    Dodaj osobe u svoju grupu.
                                </p>
                                <p className="text-sm font-medium text-rose-500 mt-2">
                                    Maksimalno {maxCanAdd + 1} {maxCanAdd + 1 === 1 ? 'osoba' : maxCanAdd + 1 < 5 ? 'osobe' : 'osoba'} u grupi (uključujući tebe)
                                </p>
                            </div>

                            {/* Napomena o rezervaciji i promjeni stola */}
                            <div className="flex items-start gap-2 bg-green-50 border border-green-200 rounded-xl px-3 py-3 text-sm text-green-800">
                                <span className="mt-0.5 flex-shrink-0">🪑</span>
                                <span>
                                    Kada odaberete stol u sljedećem koraku, rezervacija vrijedi za <strong>tebe i sve osobe u grupi</strong>. Gosti s vlastitim linkom mogu <strong>samostalno promijeniti stol</strong> ako žele. Dodatna mjesta ostaju vezana uz stol koji odabereš.
                                </span>
                            </div>

                            {/* Search za registrirane */}
                            {weddingInfo?.allowGroupReservation && (
                                <div className="flex flex-col gap-2">
                                    <label className="text-sm font-semibold text-gray-700">Gosti s popisa</label>
                                    <div className="relative" ref={searchRef}>
                                        <div className="flex items-center gap-2 bg-white border border-gray-300 rounded-xl px-4 py-2.5 focus-within:border-rose-400 focus-within:ring-2 focus-within:ring-rose-400/20 transition">
                                            <FaSearch className="text-gray-400 text-sm flex-shrink-0" />
                                            <input
                                                type="text"
                                                value={groupSearch}
                                                onChange={(e) => setGroupSearch(e.target.value)}
                                                placeholder="Traži ime i prezime..."
                                                className="flex-1 bg-transparent focus:outline-none text-sm text-gray-800 placeholder-gray-400"
                                            />
                                            {searchLoading && <LoadingForButton />}
                                        </div>

                                        {groupSearchResults.length > 0 && (
                                            <div className="absolute w-full mt-1 z-50 bg-white border border-gray-200 rounded-xl shadow-lg overflow-hidden">
                                                {selectedGuests.length + withoutInviteCount >= maxCanAdd ? (
                                                    <p className="px-4 py-3 text-sm text-gray-400 text-center">
                                                        Dostigli ste maksimalan broj osoba u grupi.
                                                    </p>
                                                ) : (
                                                    groupSearchResults.map((g) => (
                                                        <button
                                                            key={g._id}
                                                            type="button"
                                                            onMouseDown={() => addGuest(g)}
                                                            className="w-full text-left px-4 py-3 hover:bg-rose-50 flex items-center justify-between group transition"
                                                        >
                                                            <span className="flex items-center gap-2 capitalize text-sm text-gray-800">
                                                                <FaUser className="text-gray-400 text-xs" />
                                                                {capitalizeWords(g.fullName)}
                                                            </span>
                                                            <span className="text-xs text-rose-500 font-medium opacity-0 group-hover:opacity-100 transition flex items-center gap-1">
                                                                <FaPlus className="text-xs" /> Dodaj
                                                            </span>
                                                        </button>
                                                    ))
                                                )}
                                            </div>
                                        )}

                                        {groupSearch.trim().length >= 2 && !searchLoading && groupSearchResults.length === 0 && (
                                            <p className="text-xs text-gray-400 mt-1 px-1">
                                                Gost nije pronađen ili već ima rezervaciju.
                                            </p>
                                        )}
                                    </div>

                                    <div className="flex items-center gap-2 bg-blue-50 border border-blue-200 rounded-xl px-3 py-2 text-sm text-blue-700">
                                        <span>ℹ️</span>
                                        {(() => {
                                            const remaining = Math.max(0, maxCanAdd - (selectedGuests.length + withoutInviteCount));
                                            if (remaining === 0) return 'Dostigli ste maksimalan broj osoba za dostupne stolove.';
                                            return `Možete dodati još ${remaining} ${remaining === 1 ? 'osobu' : remaining < 5 ? 'osobe' : 'osoba'} (ovisno o slobodnim mjestima za stolovima).`;
                                        })()}
                                    </div>

                                    <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                                        ⚠️ Goste s pozivnicom možete dodati samo jednom. Nakon potvrde možete uređivati samo dodatna mjesta.
                                    </p>
                                </div>
                            )}

                            {/* Neregistrirani */}
                            <div className="flex flex-col gap-2">
                                <label className="text-sm font-semibold text-gray-700">Dodatna mjesta</label>
                                <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-xl px-3 py-2 text-xs text-blue-700">
                                    <span className="mt-0.5 flex-shrink-0">ℹ️</span>
                                    <span>
                                        Dodatna mjesta namijenjena su osobama <strong>bez vlastitog linka/pozivnice</strong> (npr. djeca, pratnja).
                                    </span>
                                </div>
                                <div className="flex items-center justify-between bg-white border border-gray-300 rounded-xl px-4 py-3">
                                    <span className="text-sm text-gray-600">Broj osoba</span>
                                    <div className="flex items-center gap-4">
                                        <button
                                            onClick={() => setWithoutInviteCount(c => Math.max(0, c - 1))}
                                            disabled={withoutInviteCount === 0}
                                            className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:border-rose-400 hover:text-rose-500 disabled:opacity-30 disabled:cursor-not-allowed transition"
                                        >
                                            <FaMinus className="text-xs" />
                                        </button>
                                        <span className="text-lg font-semibold text-gray-800 w-4 text-center">{withoutInviteCount}</span>
                                        <button
                                            onClick={() => setWithoutInviteCount(c => Math.min(maxUnregistered, c + 1))}
                                            disabled={withoutInviteCount >= maxUnregistered || !canAddMore}
                                            className="w-8 h-8 rounded-full border border-gray-300 flex items-center justify-center text-gray-600 hover:border-rose-400 hover:text-rose-500 disabled:opacity-30 disabled:cursor-not-allowed transition"
                                        >
                                            <FaPlus className="text-xs" />
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* Summary grupe */}
                            {(selectedGuests.length > 0 || withoutInviteCount > 0) && (
                                <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4 flex flex-col gap-2">
                                    <p className="text-sm font-semibold text-rose-700 mb-1">Tvoja grupa</p>
                                    <div className="flex items-center gap-2 text-sm text-gray-700">
                                        <FaCheck className="text-green-500 text-xs flex-shrink-0" />
                                        <span className="capitalize font-medium">{capitalizeWords(guest.fullName)}</span>
                                        <span className="text-xs text-gray-400">(ti)</span>
                                    </div>
                                    {selectedGuests.map((g) => (
                                        <div key={g._id} className="flex items-center justify-between gap-2 text-sm text-gray-700">
                                            <div className="flex items-center gap-2">
                                                <FaCheck className="text-green-500 text-xs flex-shrink-0" />
                                                <span className="capitalize">{capitalizeWords(g.fullName)}</span>
                                            </div>
                                            <button onClick={() => removeGuest(g._id)} className="text-gray-400 hover:text-red-500 transition">
                                                <FaTimes className="text-xs" />
                                            </button>
                                        </div>
                                    ))}
                                    {withoutInviteCount > 0 && (
                                        <div className="flex items-center gap-2 text-sm text-gray-700">
                                            <FaCheck className="text-green-500 text-xs flex-shrink-0" />
                                            <span>+{withoutInviteCount} dodatna mjesta</span>
                                        </div>
                                    )}
                                    <div className="border-t border-rose-200 mt-1 pt-2 text-sm text-rose-700 font-semibold">
                                        Ukupno: {totalGroupSize} {totalGroupSize === 1 ? 'osoba' : totalGroupSize < 5 ? 'osobe' : 'osoba'}
                                    </div>
                                </div>
                            )}

                            <div className="flex flex-col gap-3 mt-1">
                                <button
                                    onClick={handleGroupConfirm}
                                    disabled={rsvpLoading || (selectedGuests.length === 0 && withoutInviteCount === 0)}
                                    className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl text-white font-semibold text-lg bg-gradient-to-r from-rose-500 to-pink-600 shadow-lg shadow-rose-500/30 hover:opacity-90 transition disabled:opacity-50"
                                >
                                    {rsvpLoading ? <LoadingForButton /> : <><FaCheck /> Potvrdi grupu ({totalGroupSize})</>}
                                </button>
                                <button
                                    onClick={() => setGroupMode(null)}
                                    className="w-full py-3 text-gray-500 hover:text-gray-700 transition text-center"
                                >
                                    ← Natrag
                                </button>
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    };

    // ─── KORAK 3: Odabir stola ────────────────────────────────────────────────
    const renderTable = () => (
        <div className="w-full">
            <div className="flex justify-center mb-4 px-4">
                <button
                    onClick={() => setStep(
                        guest.addedByGuestName && !guest.rsvpDate
                            ? STEPS.ADDED_BY_GUEST
                            : !guest.rsvpDate
                                ? STEPS.GROUP
                                : STEPS.CONFIRMED
                    )}
                    className="text-gray-500 hover:text-gray-700 transition"
                >
                    ← Natrag
                </button>

            </div>
            <p className="text-center text-gray-700 font-medium text-lg mb-4">
                👋 {capitalizeWords(guest.fullName)}
            </p>
            <TablesReservation
                tables={tablesWithGuests}
                wedding={weddingInfo}
                setShowNotification={setShowNotification}
                setNotificationMessage={setNotificationMessage}
                setErrorResponse={setErrorResponse}
                handleCancelGroup={handleCancelGroup}
            />
        </div>
    );

    // ─── CONFIRMED ────────────────────────────────────────────────────────────
    const renderConfirmed = () => {
        const tableName = tablesWithGuests?.find(t => t._id === guest.reservedTableId)?.name;
        const isAddedByGuest = !!guest.addedByGuestName;

        return (
            <div className="flex flex-col items-center px-4 pb-10 mt-6">
                <div className="w-full max-w-sm flex flex-col gap-4">
                    {guest.isAttending ? (
                        <>
                            <div className="bg-green-50 border border-green-200 rounded-2xl p-5 text-center">
                                <FaCheck className="text-green-500 text-3xl mx-auto mb-2" />
                                <h2 className="text-lg font-bold text-gray-800 mb-1">Dolazak potvrđen!</h2>
                                <p className="text-gray-500 text-sm">Vidimo se na svadbenoj večeri.</p>

                                {/* Prikaži info o tome tko ga je dodao */}
                                {isAddedByGuest && (
                                    <p className="text-blue-600 text-xs mt-2">
                                        Dodao/la vas: <strong className="capitalize">{capitalizeWords(guest.addedByGuestName)}</strong>
                                    </p>
                                )}

                                {/* Companions — samo ako nije dodan od drugog gosta */}
                                {!isAddedByGuest && (guest.companions?.registered?.length > 0 || guest.companions?.unregistered > 0) && (
                                    <div className="text-gray-500 text-sm mt-2 flex flex-col gap-1">
                                        {guest.companions.registered.map((r) => (
                                            <p key={r._id || r}>+ {capitalizeWords(r.fullName || r)}</p>
                                        ))}
                                        {guest.companions.unregistered > 0 && (
                                            <p>+ {guest.companions.unregistered} dodatno mjesto</p>
                                        )}
                                    </div>
                                )}

                                {tableName && (
                                    <>
                                        <p className="text-green-600 font-medium text-sm mt-3">Stol rezerviran ✓</p>
                                        <p className="text-gray-500 text-sm mt-1 capitalize">
                                            Stol: <strong>{tableName}</strong>
                                        </p>
                                    </>
                                )}
                            </div>

                            {!tableName && (
                                <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 text-center">
                                    <p className="text-amber-700 font-semibold text-sm">⚠️ Stol još nije odabran</p>
                                    <p className="text-amber-600 text-xs mt-1">
                                        Možeš ga odabrati sada ili kasnije. Ako ne odabereš, mladenci će ga dodijeliti za tebe.
                                    </p>
                                </div>
                            )}

                            <button
                                onClick={() => setStep(STEPS.TABLE)}
                                className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl text-white font-semibold bg-gradient-to-r from-rose-500 to-pink-600 shadow-lg shadow-rose-500/30 hover:opacity-90 transition"
                            >
                                {tableName ? 'Promijeni stol / pogledaj raspored' : 'Odaberi stol'}
                            </button>

                            {/* ── Gost dodan od drugog gosta ── */}
                            {isAddedByGuest ? (
                                <>
                                    {/* Dodatna mjesta — samo ako ima slobodnih mjesta za stolom */}
                                    {tableName && freeSeatsOnCurrentTable > 0 && (
                                        <button
                                            onClick={() => {
                                                setWithoutInviteCount(guest.companions?.unregistered || 0);
                                                setStep(STEPS.GROUP);
                                            }}
                                            className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-gray-600 font-medium bg-white border border-gray-300 hover:bg-gray-50 transition text-sm"
                                        >
                                            <FaEdit /> Uredi dodatna mjesta
                                        </button>
                                    )}
                                    {/* [IZMJENA] Otvori confirm modal umjesto direktnog poziva */}
                                    <button
                                        onClick={() => setShowCancelGroupConfirm(true)}
                                        disabled={rsvpLoading}
                                        className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-gray-600 font-medium bg-white border border-gray-300 hover:bg-gray-50 transition text-sm"
                                    >
                                        Izađi iz grupe
                                    </button>
                                    <button
                                        onClick={() => setShowNotAttendingConfirm(true)}
                                        disabled={rsvpLoading}
                                        className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-gray-600 font-medium bg-white border border-gray-300 hover:bg-gray-50 transition text-sm"
                                    >
                                        {rsvpLoading ? <LoadingForButton /> : <><FaTimes /> Ne mogu doći</>}
                                    </button>
                                </>
                            ) : (
                                /* ── Normalni gost ── */
                                <>
                                    {guest.groupReservationUsed ? (
                                        <>
                                            <button
                                                onClick={() => {
                                                    setWithoutInviteCount(guest.companions?.unregistered || 0);
                                                    setStep(STEPS.GROUP);
                                                }}
                                                className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-gray-600 font-medium bg-white border border-gray-300 hover:bg-gray-50 transition text-sm"
                                            >
                                                <FaEdit /> Uredi dodatna mjesta
                                            </button>
                                            <button
                                                onClick={() => setShowNotAttendingConfirm(true)}
                                                disabled={rsvpLoading}
                                                className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-gray-600 font-medium bg-white border border-gray-300 hover:bg-gray-50 transition text-sm"
                                            >
                                                {rsvpLoading ? <LoadingForButton /> : <><FaTimes /> Ne mogu doći</>}
                                            </button>
                                        </>
                                    ) : (
                                        <button
                                            onClick={() => setStep(STEPS.ATTENDANCE)}
                                            className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-gray-600 font-medium bg-white border border-gray-300 hover:bg-gray-50 transition text-sm"
                                        >
                                            <FaEdit /> Uredi dolazak
                                        </button>
                                    )}
                                </>
                            )}
                        </>
                    ) : (
                        <>
                            <div className="bg-gray-50 border border-gray-200 rounded-2xl p-5 text-center">
                                <FaTimes className="text-gray-400 text-3xl mx-auto mb-2" />
                                <h2 className="text-lg font-bold text-gray-800 mb-1">Označili ste da ne dolazite.</h2>
                                <p className="text-gray-500 text-sm">Vaš odgovor je zabilježen.</p>
                            </div>
                            <button
                                onClick={() => setStep(STEPS.ATTENDANCE)}
                                className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-gray-600 font-medium bg-white border border-gray-300 hover:bg-gray-50 transition text-sm"
                            >
                                <FaEdit /> Promijeni odluku
                            </button>
                        </>
                    )}
                </div>
            </div>
        );
    };

    // ─── ADDED BY GUEST ───────────────────────────────────────────────────────
    const renderAddedByGuest = () => {
        const tableName = tablesWithGuests?.find(t => t._id === guest.reservedTableId)?.name;
        if (!tableName) {
            return (
                <div className="flex flex-col items-center px-4 pb-10 mt-6">
                    <div className="w-full max-w-sm flex flex-col gap-4">
                        <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 text-center">
                            <FaUserFriends className="text-blue-400 text-3xl mx-auto mb-3" />
                            <h2 className="text-lg font-bold text-gray-800 mb-2">
                                Dodani ste u grupu
                            </h2>
                            <p className="text-gray-600 text-sm">
                                <strong className="capitalize">
                                    {capitalizeWords(guest.addedByGuestName)}
                                </strong> vas je dodao/la u svoju grupu. Stol još nije odabran — bit ćete obaviješteni kada bude rezerviran.
                            </p>
                        </div>

                        <button
                            onClick={() => setStep(STEPS.TABLE)}
                            className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-gray-600 font-medium bg-white border border-gray-300 hover:bg-gray-50 transition text-sm"
                        >
                            🪑 Pogledaj raspored stolova
                        </button>

                        <button
                            onClick={() => setShowCancelGroupConfirm(true)}
                            disabled={rsvpLoading}
                            className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-gray-600 font-medium bg-white border border-gray-300 hover:bg-gray-50 transition text-sm"
                        >
                            Izađi iz grupe
                        </button>

                        <button
                            onClick={() => setShowNotAttendingConfirm(true)}
                            disabled={rsvpLoading}
                            className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-gray-600 font-medium bg-white border border-gray-300 hover:bg-gray-50 transition text-sm"
                        >
                            <FaTimes /> Nažalost ne mogu doći
                        </button>
                    </div>
                </div>
            );
        }
        return (
            <div className="flex flex-col items-center px-4 pb-10 mt-6">
                <div className="w-full max-w-sm flex flex-col gap-4">
                    <div className="bg-blue-50 border border-blue-200 rounded-2xl p-5 text-center">
                        <FaUserFriends className="text-blue-400 text-3xl mx-auto mb-3" />
                        <h2 className="text-lg font-bold text-gray-800 mb-2">Rezervirani ste za stol</h2>
                        <p className="text-gray-600 text-sm">
                            <strong className="capitalize">
                                {capitalizeWords(guest.addedByGuestName)}
                            </strong> vas je smjestio/la za stol{' '}
                            {tableName && <strong className="capitalize">{tableName}</strong>}.
                        </p>
                    </div>

                    <button
                        onClick={handleConfirmAddedByGuest}
                        disabled={rsvpLoading}
                        className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl text-white font-semibold text-lg bg-gradient-to-r from-rose-500 to-pink-600 shadow-lg shadow-rose-500/30 hover:opacity-90 transition"
                    >
                        {rsvpLoading ? <LoadingForButton /> : <><FaCheck /> Potvrđujem dolazak</>}
                    </button>

                    <button
                        onClick={() => setStep(STEPS.TABLE)}
                        className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-gray-600 font-medium bg-white border border-gray-300 hover:bg-gray-50 transition text-sm"
                    >
                        Pogledaj raspored stolova / promijeni stol
                    </button>

                    {/* [IZMJENA] Otvori confirm modal umjesto direktnog poziva */}
                    <button
                        onClick={() => setShowCancelGroupConfirm(true)}
                        disabled={rsvpLoading}
                        className="w-full flex items-center justify-center gap-2 py-3 rounded-2xl text-gray-600 font-medium bg-white border border-gray-300 hover:bg-gray-50 transition text-sm"
                    >
                        Izađi iz grupe
                    </button>

                    <button
                        onClick={() => setShowNotAttendingConfirm(true)}
                        disabled={rsvpLoading}
                        className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl text-gray-600 font-semibold text-lg bg-white border border-gray-300 hover:bg-gray-50 transition"
                    >
                        {rsvpLoading ? <LoadingForButton /> : <><FaTimes /> Nažalost ne mogu doći</>}
                    </button>

                </div>
            </div>
        );
    };

    const renderStepIndicator = () => {
        if (step === STEPS.ADDED_BY_GUEST) {
            return (
                <div className="flex items-center justify-center mb-6 px-4">
                    <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-2xl px-5 py-3">
                        <span className="text-xl">👥</span>
                        <div>
                            <p className="text-sm font-semibold text-blue-700">Dodani ste u grupu</p>
                            <p className="text-xs text-blue-500">Potvrdite dolazak ili odaberite drugi stol</p>
                        </div>
                    </div>
                </div>
            );
        }

        const hasGroup = (guest.companions?.registered?.length > 0) || (guest.companions?.unregistered > 0);

        // Na CONFIRMED — prikaži sve korake kao završene, ali solo gost nema Grupa korak
        const steps = step === STEPS.CONFIRMED
            ? hasGroup
                ? [
                    { key: STEPS.ATTENDANCE, label: 'Dolazak', icon: '✉️' },
                    { key: STEPS.GROUP, label: 'Grupa', icon: '👥' },
                    { key: STEPS.TABLE, label: 'Stol', icon: '🪑' },
                ]
                : [
                    { key: STEPS.ATTENDANCE, label: 'Dolazak', icon: '✉️' },
                    { key: STEPS.TABLE, label: 'Stol', icon: '🪑' },
                ]
            : [
                { key: STEPS.ATTENDANCE, label: 'Dolazak', icon: '✉️' },
                { key: STEPS.GROUP, label: 'Grupa', icon: '👥' },
                { key: STEPS.TABLE, label: 'Stol', icon: '🪑' },
            ];

        // Na CONFIRMED svi su završeni (activeIndex = steps.length)
        const activeIndex = step === STEPS.CONFIRMED
            ? guest.reservedTableId
                ? steps.length        // ima stol → sve zeleno
                : steps.length - 1   // nema stol → zadnji korak (Stol) je aktivan/čeka
            : steps.findIndex(s => s.key === step);

        if (activeIndex === -1) return null;

        return (
            <div className="flex items-center justify-center gap-2 mb-6 px-4">
                {steps.map((s, i) => {
                    const isDone = i < activeIndex;
                    const isActive = i === activeIndex;
                    return (
                        <React.Fragment key={s.key}>
                            <div className="flex flex-col items-center gap-1">
                                <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold transition-all
                                ${isDone ? 'bg-green-500 text-white'
                                        : isActive ? 'bg-rose-500 text-white shadow-lg shadow-rose-300'
                                            : 'bg-gray-200 text-gray-400'}`}>
                                    {isDone ? '✓' : s.icon}
                                </div>
                                <span className={`text-xs font-medium transition-all
                                ${isActive ? 'text-rose-500' : isDone ? 'text-green-500' : 'text-gray-400'}`}>
                                    {s.label}
                                </span>
                            </div>
                            {i < steps.length - 1 && (
                                <div className={`h-0.5 w-10 mb-4 rounded transition-all
                                ${i < activeIndex ? 'bg-green-400' : 'bg-gray-200'}`} />
                            )}
                        </React.Fragment>
                    );
                })}
            </div>
        );
    };

    // ─── MAIN RENDER ──────────────────────────────────────────────────────────
    return (
        <>
            <Helmet>
                <title className="capitalize">
                    {weddingInfo?.brideName && weddingInfo?.groomName
                        ? `${capitalizeWords(weddingInfo.brideName)} & ${capitalizeWords(weddingInfo.groomName)} – ${t('meta.reservation.title')}`
                        : t('meta.reservation.titleFallback')}
                </title>
                <meta name="description" content={t('meta.reservation.description')} />
            </Helmet>

            <div className="min-h-screen bg-gradient-to-br from-cyan-200 via-white to-pink-300 flex flex-col">
                <main className="flex-1">

                    {/* Navbar */}
                    <div className="flex justify-between items-center px-4 py-4 lg:px-10 relative">
                        <Link to="/" className="flex items-center gap-2 shrink-0 no-underline">
                            <img src="/logo2.png" className="w-8 h-8 md:w-9 md:h-9" alt="Weddinfy" />
                            <span className="font-display text-xl md:text-[22px] font-semibold text-stone-900">Weddinfy</span>
                        </Link>
                        <div className="flex items-center space-x-4">
                            {/* Language picker */}
                            {/* <div className="relative" tabIndex={0} onBlur={() => setOpenDropdown(null)}>
                                <div
                                    className="bg-white border border-gray-500 rounded-xl px-2 cursor-pointer flex items-center"
                                    onClick={() => setOpenDropdown(prev => prev === 'language' ? null : 'language')}
                                >
                                    <span className="text-gray-800 uppercase">
                                        {i18n.language === 'hr' && '🌐'}
                                        {i18n.language === 'en' && '🇬🇧'}
                                        {i18n.language === 'de' && '🇩🇪'}
                                    </span>
                                    {openDropdown === 'language'
                                        ? <FaChevronUp className="ml-2 text-gray-600" />
                                        : <FaChevronDown className="ml-2 text-gray-600" />}
                                </div>
                                {openDropdown === 'language' && (
                                    <div className="absolute right-0 mt-1 z-50 w-full min-w-[130px] bg-white border border-gray-500 rounded-xl shadow-lg">
                                        {[
                                            { code: 'hr', label: 'Jezik', flag: '🌐' },
                                            { code: 'en', label: 'English', flag: '🇬🇧' },
                                            { code: 'de', label: 'Deutsch', flag: '🇩🇪' },
                                        ].map((lang) => (
                                            <button
                                                key={lang.code}
                                                type="button"
                                                onMouseDown={() => {
                                                    i18n.changeLanguage(lang.code);
                                                    localStorage.setItem('lang', lang.code);
                                                    setOpenDropdown(null);
                                                }}
                                                className="w-full text-left px-4 py-2 text-gray-800 hover:bg-gray-100 rounded-xl flex items-center gap-2"
                                            >
                                                <span className="text-xl">{lang.flag}</span>
                                                <span>{lang.label}</span>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div> */}

                            {isGuestLoggedIn && (
                                <button
                                    onClick={() => setShowLogoutConfirm(true)}
                                    className="text-red-600 hover:text-red-800 flex items-center space-x-2 py-2"
                                >
                                    <FaSignOutAlt className="text-lg" />
                                    <span>{t('logout')}</span>
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Notifikacije */}
                    {showNotification && (
                        <NotificationBanner
                            message={notificationMessage}
                            onClose={() => setShowNotification(false)}
                        />
                    )}
                    {errorResponse && (
                        <NotificationBanner
                            message={t('genericError')}
                            onClose={() => setShowNotification(false)}
                            errorResponse={errorResponse}
                        />
                    )}
                    {showNotAttendingConfirm && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
                            <div className="bg-white p-6 rounded-2xl shadow-lg w-full max-w-sm text-center space-y-4">
                                <FaTimes className="text-gray-400 text-3xl mx-auto" />
                                <h3 className="text-lg font-semibold text-gray-800">Potvrda odustajanja</h3>
                                <p className="text-sm text-gray-500">
                                    Označit ćete da ne dolazite. Vaše mjesto za stolom bit će oslobođeno.
                                </p>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => { setShowNotAttendingConfirm(false); handleAttendance(false); }}
                                        disabled={rsvpLoading}
                                        className="flex-1 bg-red-500 text-white py-3 rounded-xl font-medium hover:bg-red-600 transition"
                                    >
                                        {rsvpLoading ? <LoadingForButton /> : 'Ne dolazim'}
                                    </button>
                                    <button
                                        onClick={() => setShowNotAttendingConfirm(false)}
                                        className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-medium hover:bg-gray-200 transition"
                                    >
                                        Odustani
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* [NOVO] Modal za potvrdu izlaska iz grupe */}
                    {showCancelGroupConfirm && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
                            <div className="bg-white p-6 rounded-2xl shadow-lg w-full max-w-sm text-center space-y-4">
                                <FaUserFriends className="text-gray-400 text-3xl mx-auto" />
                                <h3 className="text-lg font-semibold text-gray-800">Izlaz iz grupe</h3>
                                <p className="text-sm text-gray-500">
                                    Izlaskom iz grupe vaša rezervacija stola bit će poništena. Moći ćete samostalno odabrati stol.
                                </p>
                                <div className="flex gap-2">
                                    <button
                                        onClick={() => { setShowCancelGroupConfirm(false); handleCancelGroup(); }}
                                        disabled={rsvpLoading}
                                        className="flex-1 bg-red-500 text-white py-3 rounded-xl font-medium hover:bg-red-600 transition"
                                    >
                                        {rsvpLoading ? <LoadingForButton /> : 'Izađi iz grupe'}
                                    </button>
                                    <button
                                        onClick={() => setShowCancelGroupConfirm(false)}
                                        className="flex-1 bg-gray-100 text-gray-700 py-3 rounded-xl font-medium hover:bg-gray-200 transition"
                                    >
                                        Odustani
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Logout modal */}
                    {showLogoutConfirm && (
                        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 px-4">
                            <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-sm text-center space-y-4">
                                <h3 className="text-lg font-semibold text-gray-800">{t('logoutConfirmTitle')}</h3>
                                <div className="flex justify-end space-x-2 mt-4">
                                    <button
                                        onClick={handleLogout}
                                        className="bg-red-500 text-white px-4 py-2 rounded hover:bg-red-700 transition"
                                    >
                                        {t('logout')}
                                    </button>
                                    <button
                                        onClick={() => setShowLogoutConfirm(false)}
                                        className="bg-gray-300 text-gray-700 px-4 py-2 rounded hover:bg-gray-400 transition"
                                    >
                                        {t('cancel')}
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Header (pozivnica / info) */}
                    <div className={`transition-all duration-500 ${showInvitation && weddingInfo?.invitationUrl
                        ? 'lg:flex lg:flex-row lg:items-start lg:justify-center lg:gap-12 lg:px-16 lg:pt-6'
                        : 'flex flex-col items-center'
                        }`}>
                        {/* Pozivnica — lijevo na desktopu */}
                        {renderHeader()}

                        {/* Desna strana — step indicator + content */}
                        <div className={`w-full transition-all duration-500 ${showInvitation && weddingInfo?.invitationUrl
                            ? 'lg:max-w-md'
                            : 'max-w-full'
                            }`}>
                            {isGuestLoggedIn && showInvitation && weddingInfo?.invitationUrl && (
                                <p className="hidden lg:block text-center text-gray-700 font-medium text-lg mb-4">
                                    👋 {capitalizeWords(guest.fullName)}
                                </p>
                            )}
                            {!loading && !guest.isFamilyGroup && renderStepIndicator()}

                            {loading ? (
                                <div className="flex items-center justify-center mt-20">
                                    <LoadingForButton />
                                </div>
                            ) : isGuestLoggedIn && guest.isFamilyGroup ? (
                                <FamilyReservationFlow
                                    guest={guest}
                                    weddingInfo={weddingInfo}
                                    tablesWithGuests={tablesWithGuests}
                                    setShowNotification={setShowNotification}
                                    setNotificationMessage={setNotificationMessage}
                                    setErrorResponse={setErrorResponse}
                                    setFamilyShowInvitation={setFamilyShowInvitation}
                                />
                            ) : (
                                <>
                                    {step === STEPS.ADDED_BY_GUEST && isGuestLoggedIn && renderAddedByGuest()}
                                    {step === STEPS.ATTENDANCE && isGuestLoggedIn && renderAttendance()}
                                    {step === STEPS.GROUP && isGuestLoggedIn && renderGroup()}
                                    {step === STEPS.TABLE && isGuestLoggedIn && renderTable()}
                                    {step === STEPS.CONFIRMED && isGuestLoggedIn && renderConfirmed()}
                                </>
                            )}
                        </div>
                    </div>


                </main>
                <footer><Footer /></footer>
            </div>
        </>
    );
}

export default ReservationPage;
